import Http, { IHttp } from 'lib/http';
import axios, { CancelTokenSource } from 'axios';
import { IPlace } from 'Modules/Places/types';
import {
  IChangeOrderResponse,
  IGetOrderResponse,
  IClearOrderResponse,
  IFillOrderResponse,
  IFillOrderRequest,
  IActiveOrdersList,
  IAddProductToOrderRequest,
  IAddPromocodeResponse,
} from './types';

export interface IOrdersHttp {
  changeOrder(
    shop_id: number,
    product_id: number,
    operation: string,
    amount?: number,
    weight?: number,
    zone_id?: number
  ): Promise<IChangeOrderResponse>;

  getOrder(
    shop_id: number,
    place?: IPlace,
    delivery_point_id?: number,
    zone_id?: number,
    fetcher?: typeof Http
  ): Promise<IGetOrderResponse>;

  fillOrder(order: IFillOrderRequest): Promise<IFillOrderResponse>;

  clearOrder(shop_id: number): Promise<IClearOrderResponse>;

  getActiveOrders(shop_id: number): Promise<IActiveOrdersList>;

  addProductToOrder(props: IAddProductToOrderRequest): Promise<IChangeOrderResponse>;
}
const cancelTokens: Map<number, CancelTokenSource> = new Map<number, CancelTokenSource>();
export class OrdersHttp implements IOrdersHttp {
  private readonly http: IHttp = Http;

  public cancelProductChangeRequest(product_id: number) {
    cancelTokens.get(product_id)?.cancel();
  }

  public changeOrder(
    shop_id: number,
    product_id: number,
    operation: string,
    amount?: number,
    weight?: number,
    zone_id?: number
  ): Promise<IChangeOrderResponse> {
    /**
     * отмена запросов нужна вот для какого кейса:
     * - юзер натыкивает один товар
     * - уходит запрос
     * - пока запрос резолвится юзер меняет количество товара
     * - уходит второй запрос
     * - если запрос не отменен, то количество товара начинает плясать, потому
     * что зарезолвился первый запрос и количество взялось из него, а потом
     * из второго запроса
     *
     * при этом нужно чтобы отменялись запросы только для одного товара, поэтому
     * они раскидываются по айдишникам товаров
     */
    this.cancelProductChangeRequest(product_id);
    cancelTokens.set(product_id, axios.CancelToken.source());
    return this.http.patch(
      `shops/${shop_id}/order/change`,
      {
        product_id,
        operation,
        ...(amount && { amount }),
        ...(weight && { weight }),
        ...(zone_id && { zone_id }),
      },
      {
        cancelToken: cancelTokens.get(product_id)?.token,
      }
    );
  }

  public getOrder(
    shop_id: number,
    place?: IPlace,
    delivery_point_id?: number,
    zone_id?: number,
    fetcher = Http
  ): Promise<IGetOrderResponse> {
    return fetcher.post(`shops/${shop_id}/order`, {
      ...(place && { place }),
      ...(delivery_point_id && { delivery_point_id }),
      ...(zone_id && { zone_id }),
    });
  }

  public clearOrder(shop_id: number): Promise<IClearOrderResponse> {
    return this.http.post(`shops/${shop_id}/order/clear`);
  }

  public addPromocode(promocode: string): Promise<IAddPromocodeResponse> {
    return this.http.post(`order/promocode/add`, {
      promocode,
    });
  }

  public removePromocode(): Promise<IChangeOrderResponse> {
    return this.http.delete(`order/promocode/remove`);
  }

  public spendPoints(points: number): Promise<IChangeOrderResponse> {
    return this.http.patch(`order/points`, {
      points,
    });
  }

  public addCommentToProduct(product_id: number, comment: string, order_id: number): Promise<IChangeOrderResponse> {
    return this.http.patch(`order/products/${product_id}/comment`, {
      comment,
      order_id,
    });
  }

  public fillOrder(order: IFillOrderRequest): Promise<IFillOrderResponse> {
    return this.http.post('order/fill', order);
  }

  public productClaim(orderID: number, productIDS: number[], comment: string, photos: Blob[]): Promise<{}> {
    const formData = new FormData();

    formData.append('product_ids', `[${productIDS.join(',')}]`);
    formData.append('comment', comment);

    photos.map((p, i: number) => formData.append(`photo${i}`, p, '.jpg'));

    return this.http.post(`/orders/${orderID}/claim`, formData);
  }

  public getActiveOrders(shop_id: number): Promise<IActiveOrdersList> {
    return this.http.get(`orders/active?shop_id=${shop_id}`);
  }

  public toogleFewerPlasticBags(isFewer: boolean): Promise<IChangeOrderResponse> {
    return this.http.patch('order/fewer_plastic_bags', {
      fewer_plastic_bags: isFewer,
    });
  }

  public addProductToOrder({
    order_id,
    product_id,
    amount,
    weight,
    comment,
  }: IAddProductToOrderRequest): Promise<IChangeOrderResponse> {
    return this.http.post(`orders/${order_id}/add`, {
      product_id,
      amount,
      weight,
      comment,
    });
  }
}

export default new OrdersHttp();
